"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import type { KeyboardEvent } from "react";
import { Item } from "@/lib/types";
import { formatPrice } from "@/lib/utils";
import { useCallback, useEffect, useState } from "react";
import { MapPinIcon } from "@heroicons/react/24/outline";
import OptimizedImage from "@/components/common/OptimizedImage";
import { withMemo } from "@/components/common/withMemo";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import DistanceDisplay from "@/components/common/DistanceDisplay";
import LazyLoadBoundary from "@/components/common/LazyLoadBoundary";
import ShareItem from "@/components/ui/ShareItem";
import ConfirmedAvailableBadge from "@/components/ui/ConfirmedAvailableBadge";
import { useSystemSettings } from "@/contexts/SystemSettingsContext";
import { isSellerAvailabilityConfirmed } from "@/lib/items/sellerAvailabilityConfirmed";

interface ItemCardProps {
  item: Item;
  viewMode?: "grid" | "list";
  distance?: number;
  disableDistanceAutoCalculation?: boolean;
  priority?: boolean;
  index?: number;
  disableLink?: boolean;
  onClick?: () => void; // If provided, will use onClick instead of Link navigation
  /** Admin / partner share: full item URL (with ?ref= when applicable). */
  itemShareUrl?: string;
  partnerShareToolsEnabled?: boolean;
}

function ItemCard({
  item,
  viewMode = "grid",
  distance,
  disableDistanceAutoCalculation = false,
  priority = false,
  index = 0,
  disableLink = false,
  onClick,
  itemShareUrl,
  partnerShareToolsEnabled = false,
}: ItemCardProps) {
  const router = useRouter();
  const {
    buyerAvailabilityConfirmedEnabled,
    buyerAvailabilityConfirmedWindowDays,
  } = useSystemSettings();
  const [imageError] = useState(false);
  const [isDesktopListLayout, setIsDesktopListLayout] = useState(false);
  const resolvedShareUrl = itemShareUrl ?? "";
  const hasShareControl = resolvedShareUrl.length > 0;

  const navigateToItem = useCallback(() => {
    void router.push(`/item/${item.id}`);
  }, [router, item.id]);

  useEffect(() => {
    if (!disableLink && !onClick && hasShareControl) {
      router.prefetch(`/item/${item.id}`);
    }
  }, [disableLink, onClick, hasShareControl, router, item.id]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktopListLayout(mediaQuery.matches);
    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  const runOnActionKeyDown = (e: KeyboardEvent, action: () => void) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      action();
    }
  };

  const isNewItem =
    new Date().getTime() - new Date(item.createdAt).getTime() <
    7 * 24 * 60 * 60 * 1000; // 7 days

  const showConfirmedAvailableBadge = isSellerAvailabilityConfirmed(item, {
    enabled: buyerAvailabilityConfirmedEnabled,
    windowDays: buyerAvailabilityConfirmedWindowDays,
  });

  // Determine if this item should be prioritized (above the fold)
  const shouldPrioritize = priority || index < 6; // First 6 items are likely above the fold

  const cardShellClass =
    "group relative sio-surface-card overflow-hidden h-full";
  const titleClass =
    "min-w-0 flex-1 font-medium text-foreground group-hover:text-primary line-clamp-1";
  const priceClass = "text-lg font-medium text-primary";
  const metaClass = "text-sm text-text-light";
  const newBadgeClass =
    "bg-primary text-white text-xs font-medium px-2 py-1 rounded-md";
  const newBadgeCompactClass =
    "bg-primary text-white text-xs font-medium px-1.5 py-0.5 rounded-md";

  const renderImage = (width: number, height: number, sizes: string) =>
    item.images && item.images.length > 0 && !imageError ? (
      <OptimizedImage
        src={item.images[0]}
        alt={item.title}
        width={width}
        height={height}
        className="h-full w-full object-cover"
        priority={shouldPrioritize}
        sizes={sizes}
        enableBlurPlaceholder={width >= 300}
        usage="feed-card"
      />
    ) : (
      <div className="flex h-full w-full items-center justify-center bg-secondary">
        <span className="text-2xl font-bold text-tertiary">
          {item.title.charAt(0)}
        </span>
      </div>
    );

  const shareControl = hasShareControl ? (
    <div
      className="relative z-20 shrink-0"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      role="presentation"
    >
      <ShareItem
        title={item.title}
        price={item.price}
        location={item.location}
        defects={item.defects ?? ""}
        description={item.description ?? ""}
        mainImageUrl={item.images?.[0]}
        imageUrls={item.images ?? []}
        url={resolvedShareUrl}
        partnerToolsEnabled={partnerShareToolsEnabled}
        compact
      />
    </div>
  ) : null;

  const listViewContent = (
    <div className={cardShellClass}>
      {isDesktopListLayout ? (
        <div className="flex">
          <div className="relative w-48 aspect-[4/3] flex-shrink-0 overflow-hidden">
            {renderImage(192, 144, "192px")}
            {isNewItem && (
              <div className="absolute top-3 left-3">
                <span className={newBadgeClass}>New</span>
              </div>
            )}
            {showConfirmedAvailableBadge && (
              <div className="absolute bottom-3 left-3">
                <ConfirmedAvailableBadge />
              </div>
            )}
            {(distance !== undefined ||
              (!disableDistanceAutoCalculation && item.coordinates)) && (
              <div className="absolute top-3 right-3">
                <DistanceDisplay
                  distance={distance}
                  coordinates={item.coordinates}
                  variant="badge"
                />
              </div>
            )}
          </div>
          <div className="flex-1 p-4">
            <div className="h-full flex flex-col">
              <div>
                <div className="flex items-start justify-between gap-2">
                  <h3 className={titleClass}>{item.title}</h3>
                  {shareControl}
                </div>
                <p className={`mt-1 ${metaClass} line-clamp-2`}>
                  {item.description}
                </p>
              </div>
              <div className="mt-auto">
                <div className={priceClass}>
                  {formatPrice(item.price)}
                  {item.quantity > 1 && (
                    <span className="text-sm ml-1">
                      ({item.quantity} units)
                    </span>
                  )}
                </div>
                <div className={`flex items-center ${metaClass}`}>
                  <MapPinIcon className="h-4 w-4 mr-1" />
                  {item.location}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex gap-3 p-3">
          <div className="relative h-24 w-24 flex-shrink-0 overflow-hidden rounded-sio">
            {renderImage(96, 96, "96px")}
            {isNewItem && (
              <div className="absolute top-1 left-1">
                <span className={newBadgeCompactClass}>New</span>
              </div>
            )}
            {showConfirmedAvailableBadge && (
              <div className="absolute bottom-1 left-1">
                <ConfirmedAvailableBadge compact />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className={titleClass}>{item.title}</h3>
              {shareControl}
            </div>
            <div className={`flex items-center mt-1 ${metaClass} truncate`}>
              <MapPinIcon className="h-4 w-4 mr-1" />
              {item.location}
            </div>
            <div className={`flex items-center mr-1 ${metaClass}`}>
              <div className={`mt-1 mr-3 ${priceClass}`}>
                {formatPrice(item.price)}
                {item.quantity > 1 && (
                  <span className="text-xs ml-1">({item.quantity} units)</span>
                )}
              </div>
              {(distance !== undefined ||
                (!disableDistanceAutoCalculation && item.coordinates)) && (
                <div className="mt-1">
                  <DistanceDisplay
                    distance={distance}
                    coordinates={item.coordinates}
                    variant="badge"
                    className="px-2 py-1.5 truncate"
                  />
                </div>
              )}
            </div>
            {item.description && (
              <p className={`mt-1 ${metaClass} line-clamp-1`}>
                {item.description}
              </p>
            )}
            {item.defects && (
              <p className={`mt-1 ${metaClass} line-clamp-1`}>{item.defects}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (viewMode === "list") {
    return (
      <ErrorBoundary>
        {disableLink ? (
          <div className="block">{listViewContent}</div>
        ) : onClick ? (
          hasShareControl ? (
            <div
              role="button"
              tabIndex={0}
              onClick={onClick}
              onKeyDown={(e) => runOnActionKeyDown(e, onClick)}
              className="block w-full cursor-pointer text-left"
            >
              {listViewContent}
            </div>
          ) : (
            <button
              onClick={onClick}
              className="block w-full text-left"
              type="button"
            >
              {listViewContent}
            </button>
          )
        ) : hasShareControl ? (
          <div
            role="link"
            tabIndex={0}
            onClick={navigateToItem}
            onKeyDown={(e) => runOnActionKeyDown(e, navigateToItem)}
            className="block w-full cursor-pointer text-left"
          >
            {listViewContent}
          </div>
        ) : (
          <Link href={`/item/${item.id}`} className="block">
            {listViewContent}
          </Link>
        )}
      </ErrorBoundary>
    );
  }

  const gridViewContent = (
    <div className={cardShellClass}>
      <div className="relative aspect-[4/3] w-full overflow-hidden">
        {renderImage(
          400,
          300,
          "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw",
        )}
        {isNewItem && (
          <div className="absolute top-3 left-3">
            <span className={newBadgeClass}>New</span>
          </div>
        )}
        {showConfirmedAvailableBadge && (
          <div className="absolute bottom-14 left-3 z-10">
            <ConfirmedAvailableBadge />
          </div>
        )}
        {(distance !== undefined ||
          (!disableDistanceAutoCalculation && item.coordinates)) && (
          <div className="absolute top-3 right-3">
            <DistanceDisplay
              distance={distance}
              coordinates={item.coordinates}
              variant="badge"
            />
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
          <div className="text-lg font-semibold text-white">
            {formatPrice(item.price)}
            {item.quantity > 1 && (
              <span className="text-sm ml-1">({item.quantity} units)</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col bg-surface p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className={titleClass}>{item.title}</h3>
          {shareControl}
        </div>
        <div className={`mt-2 flex items-center ${metaClass} truncate`}>
          <MapPinIcon className="h-4 w-4 mr-1" />
          {item.location}
        </div>
      </div>
    </div>
  );

  return (
    <ErrorBoundary>
      <LazyLoadBoundary>
        {disableLink ? (
          <div className="block h-full">{gridViewContent}</div>
        ) : onClick ? (
          hasShareControl ? (
            <div
              role="button"
              tabIndex={0}
              onClick={onClick}
              onKeyDown={(e) => runOnActionKeyDown(e, onClick)}
              className="block h-full w-full cursor-pointer text-left"
            >
              {gridViewContent}
            </div>
          ) : (
            <button
              onClick={onClick}
              className="block h-full w-full text-left"
              type="button"
            >
              {gridViewContent}
            </button>
          )
        ) : hasShareControl ? (
          <div
            role="link"
            tabIndex={0}
            onClick={navigateToItem}
            onKeyDown={(e) => runOnActionKeyDown(e, navigateToItem)}
            className="block h-full w-full cursor-pointer text-left"
          >
            {gridViewContent}
          </div>
        ) : (
          <Link href={`/item/${item.id}`} className="block h-full">
            {gridViewContent}
          </Link>
        )}
      </LazyLoadBoundary>
    </ErrorBoundary>
  );
}

export default withMemo(ItemCard, (prev, next) => {
  return (
    prev.item.id === next.item.id &&
    prev.item.title === next.item.title &&
    prev.item.price === next.item.price &&
    prev.viewMode === next.viewMode &&
    prev.distance === next.distance &&
    prev.disableDistanceAutoCalculation ===
      next.disableDistanceAutoCalculation &&
    prev.priority === next.priority &&
    prev.index === next.index &&
    prev.disableLink === next.disableLink &&
    prev.onClick === next.onClick &&
    prev.itemShareUrl === next.itemShareUrl &&
    prev.partnerShareToolsEnabled === next.partnerShareToolsEnabled &&
    JSON.stringify(prev.item.images) === JSON.stringify(next.item.images)
  );
});
